import React from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import { Box, Text } from '@rsos/components/capstone/base';
import {
  WrapperContainer,
  ULTagContainer,
  LiTagContainer,
  InputContainer,
  ClearIconComponent,
  ErrorComponent,
  StyledMultiEntryTextArea,
  InputTextLabel,
} from './MultiEntryInput.styles';
import { formatStartsWithOneNumber, formatPhoneNumber } from './helpers';

const spaceKeyCode = 32;

const MultiEntryInput = ({
  value,
  label,
  name,
  placeholder,
  hiddenValue,
  hiddenInputName,
  tags,
  updateTags,
  validator,
  setFieldValue,
  handleChange,
  error,
  inputType,
}) => {
  const handleRemoveTag = tag => {
    const newTags = tags.filter(t => t.id !== tag.id);
    updateTags(newTags);
    setFieldValue(hiddenInputName, newTags);
  };

  /* When the input is a phone number, to format on the fly to be like (347) 771-7556
   ** when adding and removing digits, we apply util functions.
   ** Apply different formatting functions based on whether the number starts with 1 or not */
  const handleChangeMultiEntryPhone = (e, setFieldValue) => {
    let formattedNumber;
    const { value } = e.target;
    if (value.split(/[\n,]/).length === 1) {
      const valueStartsWithOne = value.startsWith('1');
      if (valueStartsWithOne) {
        formattedNumber = formatStartsWithOneNumber(value);
      } else {
        formattedNumber = formatPhoneNumber(value);
      }
      setFieldValue('phoneNumber', formattedNumber);
    }
  };

  /* Invoked when a copy paste event is detected */
  const handlePaste = (e, setFieldValue) => {
    e.clipboardData.items[0].getAsString(text => {
      const result = text.split(/[\n,]+/).reduce((acc, curr) => {
        const trimmedValue = curr?.trim();
        if (trimmedValue) {
          acc.push(trimmedValue);
        }
        return acc;
      }, []);
      let validatedNewTag;
      if (inputType === 'tel') {
        validatedNewTag = result.map(v => {
          return {
            id: uuid(),
            value: v.startsWith('1')
              ? formatStartsWithOneNumber(v)
              : formatPhoneNumber(v),
            isValid: validator(v.startsWith('1') ? v : `1${v}`),
          };
        });
      } else {
        validatedNewTag = result.map(v => {
          return {
            value: v,
            isValid: validator(v),
            id: uuid(),
          };
        });
      }
      updateTags([...tags, ...validatedNewTag]);
      setFieldValue(name, '');
      setFieldValue(hiddenInputName, [...tags, ...validatedNewTag]);
    });
  };

  /* When Enter key or a space key is pressed we validate the input  */
  const handleKeyDown = (e, setFieldValue) => {
    const { value } = e.target;
    if ((e.key === 'Enter' || e.keyCode === spaceKeyCode) && value) {
      e.preventDefault();
      let validatedNewTag;
      if (inputType === 'tel') {
        validatedNewTag = value.split(/[\n,]/).map(v => {
          v = v.trim();
          return {
            value: v.startsWith('1')
              ? formatStartsWithOneNumber(v)
              : formatPhoneNumber(v),
            isValid: validator(v.startsWith('1') ? v : `1${v}`),
            id: uuid(),
          };
        });
      } else {
        validatedNewTag = value
          .trim()
          .split(/[ ,]+/)
          .map(v => {
            return {
              value: v,
              isValid: validator(v),
              id: uuid(),
            };
          });
      }

      updateTags([...tags, ...validatedNewTag]);
      setFieldValue(name, '');
      setFieldValue(hiddenInputName, [...tags, ...validatedNewTag]);
    }
  };
  return (
    <Box>
      {label && <InputTextLabel>{label}</InputTextLabel>}
      <WrapperContainer error={error}>
        <input type="hidden" name={hiddenInputName} value={hiddenValue} />
        <ULTagContainer>
          {tags.map((tag, i) => (
            <LiTagContainer key={tag.id} isValid={tag.isValid}>
              <Text size="small">{tag.value}</Text>
              <ClearIconComponent
                onClick={() => {
                  handleRemoveTag(tag);
                }}
              />
            </LiTagContainer>
          ))}
          <InputContainer>
            <StyledMultiEntryTextArea
              rows={1}
              name={name}
              onChange={
                inputType === 'tel'
                  ? e => handleChangeMultiEntryPhone(e, setFieldValue)
                  : handleChange
              }
              onKeyDown={e => handleKeyDown(e, setFieldValue)}
              value={value}
              placeholder={placeholder}
              onPaste={e => handlePaste(e, setFieldValue)}
            />
          </InputContainer>
        </ULTagContainer>
      </WrapperContainer>
      {error && <ErrorComponent error={error} />}
    </Box>
  );
};

MultiEntryInput.propTypes = {
  value: PropTypes.string,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  hiddenValue: PropTypes.array,
  hiddenInputName: PropTypes.string,
  tags: PropTypes.array,
  updateTags: PropTypes.func,
  validator: PropTypes.func,
  setFieldValue: PropTypes.func,
  handleChange: PropTypes.func,
  error: PropTypes.object,
  inputType: PropTypes.string,
};

export default MultiEntryInput;
